import type { BillingPlan, BridgeLimits, PlanInterval } from "../types";
import { getPlanTierLabel, getPlanTrack } from "./plan-mapping";

export const CYCLE_DAYS: Record<PlanInterval, number> = {
  monthly: 30,
  annually: 365,
};

/** Per-extra-facilitator monthly rate in ZAR, added on top of any base plan. */
export const BRIDGE_EXTRA_FACILITATOR_RATE_ZAR = 200;

/**
 * Track/tier descriptor for the segment of a plan name that sits between the
 * org label and the G/P/F code. `"Custom"` for off-tier limits; the matched
 * standard tier's own words (e.g. `"Community Starter"`) when limits line up.
 */
type PlanTierSegment = string;

/**
 * Matches only the canonical published tiers, e.g. `"Community Starter (Monthly)"`
 * or `"Programme Growth (Annual)"`. Legacy/manual-invoice/donation standard
 * plans share the same {G,P,F} limits but must never win the match (e.g.
 * `"Manual Invoice - G1P0F2 (R2000/mo)"` and `"Community Starter (Monthly)"`
 * both carry `{1,0,2}`), so the tier segment is resolved off the canonical name
 * pattern rather than limits alone.
 */
const CANONICAL_TIER_NAME =
  /^(Community|Programme)\s+(Starter|Growth|Established)\s+\((Monthly|Annual)\)$/;

/**
 * True when two limit sets describe the same allowance. `null` (unlimited) and
 * `0` (not included) are distinct, but absent counts normalise to `0` so a
 * custom plan authored with `{groups: 1, programmes: 0}` matches a seeded
 * standard tier stored the same way.
 */
function limitsMatch(a: BridgeLimits, b: BridgeLimits): boolean {
  return (
    (a.groups ?? 0) === (b.groups ?? 0) &&
    (a.programmes ?? 0) === (b.programmes ?? 0) &&
    a.facilitators === b.facilitators
  );
}

/**
 * Finds the canonical published tier whose limits, interval, and price exactly
 * match the target, returning its `"{Track} {Tier}"` label (e.g.
 * `"Community Starter"`), or `"{Track} Custom"` when nothing matches.
 *
 * The published tiers are the source of truth for what counts as "on-tier", so
 * the caller passes the live `plan_type === 'standard'` rows rather than this
 * module hardcoding the tier table. Matching requires:
 *   - a canonical tier name (excludes manual-invoice / legacy / donation rows),
 *   - the same interval,
 *   - the same {G,P,F} limits,
 *   - the same amount (guards against same-limit tiers at different prices).
 */
function resolveTierSegment(
  limits: BridgeLimits,
  interval: PlanInterval,
  amount: number,
  standardPlans: readonly BillingPlan[]
): PlanTierSegment {
  const fallbackTrack = (limits.groups ?? 0) > 0 ? "Community" : "Programme";

  const match = standardPlans.find(
    (plan) =>
      plan.plan_type === "standard" &&
      plan.interval === interval &&
      plan.amount === amount &&
      CANONICAL_TIER_NAME.test(plan.name) &&
      limitsMatch(plan.limits as BridgeLimits, limits)
  );

  if (!match) {
    return `${fallbackTrack} Custom`;
  }

  const track =
    getPlanTrack(match) === "programmes" ? "Programme" : "Community";
  const tier = getPlanTierLabel(match);
  return `${track} ${tier}`;
}

/**
 * Produces the display name for an org-scoped custom Bridge plan.
 *
 * Format: `{orgLabel} - {Track} Custom - G{groups}P{programmes}F{facilitators} ({Interval})`
 *
 * orgLabel is the org name when known (e.g. "Anthem Recovery"), or the org UUID
 * when the org hasn't been created yet (e.g. during initial checkout).
 * The lookup key is now organization_id on subscription_plans, not this name.
 *
 * Community = groups > 0; Programme = groups == 0 (or null treated as 0).
 */
export function formatCustomPlanName(
  limits: BridgeLimits,
  interval: PlanInterval,
  orgLabel: string
): string {
  const groups = limits.groups ?? 0;
  const programmes = limits.programmes ?? 0;
  const track = groups > 0 ? "Community" : "Programme";
  const intervalLabel = interval === "monthly" ? "Monthly" : "Annual";
  return `${orgLabel} - ${track} Custom - G${groups}P${programmes}F${limits.facilitators} (${intervalLabel})`;
}

/**
 * Like `formatCustomPlanName`, but drops the word "Custom" when the limits,
 * interval, and price exactly match a canonical published tier. For example, a plan
 * sized and priced to Community Starter reads
 * `"{org} - Community Starter - G1P0F2 (Monthly)"` instead of
 * `"… Community Custom - G1P0F2 …"`. The G/P/F code is always retained so the
 * allowance stays legible and per-org Paystack plan names remain distinct.
 *
 * `amount` is the plan's price in base currency units (ZAR), matched against the
 * candidate tier's `amount` so a same-limit tier at a different price never
 * masquerades as on-tier. Off-tier plans fall back to `formatCustomPlanName`.
 */
export function formatPlanNameForLimits(
  limits: BridgeLimits,
  interval: PlanInterval,
  amount: number,
  orgLabel: string,
  standardPlans: readonly BillingPlan[]
): string {
  const groups = limits.groups ?? 0;
  const programmes = limits.programmes ?? 0;
  const intervalLabel = interval === "monthly" ? "Monthly" : "Annual";
  const segment = resolveTierSegment(limits, interval, amount, standardPlans);
  return `${orgLabel} - ${segment} - G${groups}P${programmes}F${limits.facilitators} (${intervalLabel})`;
}

/**
 * Finds the cheapest canonical published tier whose group and programme
 * allowances cover `targetLimits`, at the given interval.
 *
 * Facilitators are deliberately NOT part of the cover test — they are sold as a
 * per-seat add-on on top of a tier (see `calculateCustomPlanAmount`), so a plan
 * needing more seats than its tier includes stays on that tier and pays for the
 * extra seats. Groups and programmes have no such add-on: exceeding either
 * moves the org up to the tier that covers it.
 *
 * Returns null when no published tier covers the target (limits beyond the
 * largest tier), leaving the caller to fall back to the base plan.
 */
function findCoveringTier(
  targetLimits: BridgeLimits,
  interval: PlanInterval,
  standardPlans: readonly BillingPlan[]
): BillingPlan | null {
  const targetGroups = targetLimits.groups ?? 0;
  const targetProgrammes = targetLimits.programmes ?? 0;

  const covering = standardPlans.filter((plan) => {
    if (plan.plan_type !== "standard" || plan.interval !== interval) {
      return false;
    }
    if (!CANONICAL_TIER_NAME.test(plan.name)) {
      return false;
    }
    const limits = plan.limits as BridgeLimits;
    return (
      (limits.groups ?? 0) >= targetGroups &&
      (limits.programmes ?? 0) >= targetProgrammes
    );
  });

  if (covering.length === 0) {
    return null;
  }

  return covering.reduce((cheapest, plan) =>
    plan.amount < cheapest.amount ? plan : cheapest
  );
}

/**
 * Calculates the monthly/annual amount (ZAR) for a custom plan.
 *
 * Pricing model:
 *   - Groups and programmes are TIER-priced. Needing an extra group or
 *     programme moves the org up to the cheapest published tier that covers the
 *     requested allowance — there is no per-group or per-programme add-on.
 *   - Facilitators are SEAT-priced. Seats beyond the covering tier's own
 *     facilitator allowance are charged at `perFacilitatorRate` on top.
 *
 * `standardPlans` is the live set of published plans, so the tier table stays in
 * the database rather than hardcoded here. When it is omitted — or when no tier
 * covers the target — pricing falls back to `basePlan`, preserving the previous
 * facilitator-only behaviour rather than throwing on an unpriceable request.
 *
 * The result never drops below the resolved tier's own price, so a downgrade in
 * facilitator count cannot undercut the tier the org's groups/programmes require.
 * The floor is the TIER's price, never the org's current plan — anchoring to the
 * current plan makes a seat reduction impossible to price, since that amount
 * already includes the seats being removed (GH #1200).
 */
export function calculateCustomPlanAmount(
  basePlan: BillingPlan,
  targetLimits: BridgeLimits,
  perFacilitatorRate = BRIDGE_EXTRA_FACILITATOR_RATE_ZAR,
  standardPlans: readonly BillingPlan[] = []
): number {
  const anchor =
    findCoveringTier(targetLimits, basePlan.interval, standardPlans) ??
    basePlan;

  const anchorLimits = anchor.limits as BridgeLimits;
  const anchorFacilitators = anchorLimits.facilitators ?? 0;

  const extraFacilitators = Math.max(
    0,
    targetLimits.facilitators - anchorFacilitators
  );

  return anchor.amount + extraFacilitators * perFacilitatorRate;
}

/**
 * Calculates the pro-rata upgrade charge in ZAR for a mid-cycle plan switch.
 *
 * Based on Make.com scenario 3036301:
 *   Pro Rata = (new_daily_rate - old_daily_rate) * remaining_days
 *
 * Returns 0 for downgrades or same-price plans (no refund issued).
 * The result is rounded to 2 decimal places.
 */
export function calculateProrationCharge(
  currentAmount: number,
  newAmount: number,
  nextBillingDate: string,
  interval: PlanInterval
): number {
  const dailyDiff = (newAmount - currentAmount) / CYCLE_DAYS[interval];

  if (dailyDiff <= 0) {
    return 0;
  }

  const today = new Date();
  const nextBilling = new Date(nextBillingDate);
  const remainingMs = nextBilling.getTime() - today.getTime();
  const remainingDays = Math.max(0, remainingMs / (1000 * 60 * 60 * 24));

  return Math.round(dailyDiff * remainingDays * 100) / 100;
}
